/**
 * Operating system detection utilities.
 */

import type { OperatingSystem } from "./types";
import { debugLogger } from "./debugLogger";

/**
 * Pure OS detection logic without logging.
 * Internal use only - use detectOS() for public API.
 * :return: The detected operating system type.
 */
export function _detectOSInternal(): OperatingSystem {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";

  // Check for iOS (must check before Mac, as iOS devices report Mac in user agent)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }

  // Check for Android
  if (/android/.test(userAgent)) {
    return "android";
  }

  // Check for Mac (macOS)
  if (/macintosh|mac os x|macintel/.test(userAgent) || /mac/.test(platform)) {
    return "mac";
  }

  // Check for Windows
  if (/win/.test(platform) || /windows/.test(userAgent)) {
    return "windows";
  }

  // Check for Linux
  if (
    /linux/.test(userAgent) ||
    /linux/.test(platform) ||
    /x11/.test(userAgent)
  ) {
    return "linux";
  }

  return "unknown";
}

/**
 * Detects the operating system from the user agent string.
 * :return: The detected operating system type.
 */
export function detectOS(): OperatingSystem {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || "";

  debugLogger.log("Detecting OS...", { userAgent, platform });

  const result = _detectOSInternal();

  debugLogger.logDetection("OS", result, { userAgent, platform });

  return result;
}

/**
 * Detects if the current OS is macOS.
 * :return: True if running on macOS, false otherwise.
 */
export function isMacOS(): boolean {
  return detectOS() === "mac";
}

/**
 * Detects if the current OS is Windows.
 * :return: True if running on Windows, false otherwise.
 */
export function isWindows(): boolean {
  return detectOS() === "windows";
}

/**
 * Detects if the current OS is Linux.
 * :return: True if running on Linux, false otherwise.
 */
export function isLinux(): boolean {
  return detectOS() === "linux";
}

/**
 * Detects if the current OS is a mobile platform (iOS or Android).
 * :return: True if running on a mobile OS, false otherwise.
 */
export function isMobileOS(): boolean {
  const os = detectOS();
  return os === "ios" || os === "android";
}
